import { isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import CancelablePromise from "cancelable-promise";
import Semaphore from "semaphore-promise";
import { NullGC } from "../gc/NullGC.js";
import { StandardGC } from "../gc/StandardGC.js";
import { HasResourceCleanupFeature, MediaGarbageCollector } from "../gc/MediaGarbageCollector.js";
import { MediaRef, MediaResource } from "./MediaResource.js";
import { PlayerControls, MediaContext, MediaPlayerElements } from "./Player.js";
import { MediaPlayOptions } from "./MediaPlayOptions.js";

import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection/WebViewDetails";
import { detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { acquireCancelableSemaphore } from "../../lib/utils/CancelableSemaphoreAcquire.js";
import { MediaControlOrigin } from "../events/PlayerEvent.js";
import { PreloadManagementStrategy, UnloadManagementStrategy } from "./PlayerControlsConfig.js";
import { PreloadReleaseMode } from "./PreloadReleaseMode.js";
import { MediaId, MediaInfoRawData } from "../../model/MediaInfo.js";

export interface PlayerTechFactoryConfig {
    enableGC: boolean,
    preloadReleaseMode: PreloadReleaseMode,
    preloadManagementStrategy: PreloadManagementStrategy,
    unloadManagementStrategy: UnloadManagementStrategy,
    enableReusingMediaResource: boolean,
    playOptions: MediaPlayOptions
}

export abstract class PlayerTechFactory<
    PlayerType,
    EL extends HTMLMediaElement,
    MI extends MediaInfoRawData<any>,
    REF extends MediaRef<EL, MI>,
    CTX extends MediaContext<REF>,
    RES extends MediaResource<PlayerType, EL, MI>
> implements PlayerTechFactoryInterface<EL, MI, REF, CTX>, HasResourceCleanupFeature<PlayerType, RES> {

    protected readonly _wvDetect?: DetectedWebView;
    protected logger: Logger;

    private gc: MediaGarbageCollector<PlayerType, RES>;

    private preloadMonitor: Semaphore;
    private preloadSlots: MediaId[];
    private generatedMediaResource: RES[] = [];
    protected preloadedResourcesMap: { [k: MediaId]: CancelablePromise<RES> };


    updateMuteToPreloadedMedia(muted: boolean, matchingFun: (ref: RES) => boolean) {
        const preloadedResource: CancelablePromise<RES>[] = Object.values(this.preloadedResourcesMap);
        preloadedResource.forEach(prom => prom.then(preloadedRes => {
            if (matchingFun(preloadedRes)) preloadedRes.muted(muted)
        }))
    }

    protected get currentMediaCtx(): CTX {
        return this.playerControls.getCurrentPlayingMediaContext();
    }

    protected get currentPlayerMediaElements(): MediaPlayerElements<EL> {
        return this.playerControls.fetchPlayerMediaElements(this.currentMediaCtx.ref.info.id);
    }


    constructor(
        protected playerControls: PlayerControls<EL, MI, REF, CTX>,
        protected readonly win: Window,
        public readonly config: PlayerTechFactoryConfig,
        wvDetect?: DetectedWebView) {
        this.logger = new BrowserLogger(`PlayerTechFactory`, "#8403fc")

        this._wvDetect = wvDetect ? wvDetect : detectWebView();

        this.preloadedResourcesMap = {};
        this.preloadMonitor = new Semaphore(1);
        this.preloadSlots = [];
        this.gc = this.config.enableGC ? new StandardGC<PlayerType, RES>({
            tickInSeconds: 20,
            maxResources: 10
        }, this.win, this) : new NullGC();
        this.gc.start();

    }

    private enqueuePreload(mediaID: MediaId) {
        return acquireCancelableSemaphore(this.preloadMonitor).then((release) => {
            this.preloadSlots.push(mediaID);
            release();
        });
    }

    private dequeuePreload(mediaID: MediaId) {
        return acquireCancelableSemaphore(this.preloadMonitor).then((release) => {
            this.preloadSlots = this.preloadSlots.filter(el => el != mediaID);
            release();
        });
    }


    public managePreloads(currentlyPreloadingResource: MI): CancelablePromise<void> {
        if (this.config.preloadManagementStrategy == PreloadManagementStrategy.FULL_CLEANUP) {
            return this.cancelAllEnqueuedPreloads(currentlyPreloadingResource.id);
        }
        else {
            return CancelablePromise.resolve();
        }
    }

    private cancelAllEnqueuedPreloads(except: MediaId) {
        return acquireCancelableSemaphore(this.preloadMonitor).then((release) => {
            this.preloadSlots.filter(el => el != except).forEach((mediaID) => {
                if (mediaID in this.preloadedResourcesMap) {
                    this.preloadedResourcesMap[mediaID].then(el => el.pause(MediaControlOrigin.USER));
                }
            });
            this.preloadSlots = [except];
            release();
        });
    }

    public getOrPreloadMediaContext(mediaInfo: MI, pve: MediaPlayerElements<EL>, forwardNavigation: boolean, beforePreload: (mediaIndo: MI) => MI): CancelablePromise<CTX> {

        let preloadMediaResourcePromise: CancelablePromise<RES> = this.preloadedResourcesMap[mediaInfo.id];

        if (preloadMediaResourcePromise == null || preloadMediaResourcePromise.isCanceled()) {
            beforePreload(mediaInfo)
            preloadMediaResourcePromise = this.enqueuePreload(mediaInfo.id).
                then(_ => this.generatePlayerMediaResource(mediaInfo, pve, forwardNavigation).
                    then(res => {
                        this.dequeuePreload(mediaInfo.id);
                        return res;
                    }));
        }

        this.preloadedResourcesMap[mediaInfo.id] = preloadMediaResourcePromise;

        if (this.config.enableReusingMediaResource)
            preloadMediaResourcePromise.then(res => {
                this.appendReuseableMediaResouce(res, forwardNavigation);
            })

        return this.gc.addResourceAsync(preloadMediaResourcePromise)
            .then(res => {
                return this.buildPlayerMediaContext(res, pve)
            });
    }

    cleanResource(toDestroy: RES): Promise<void> {
        if (toDestroy) {
            const mediaId = toDestroy.info.id;
            this.logger.debug("GC Destroying", mediaId, toDestroy.info);
            toDestroy.destroy();
            this.playerControls.clearPlayerMediaElement(toDestroy.info.id);
            if (mediaId in this.preloadedResourcesMap) {
                delete this.preloadedResourcesMap[mediaId];
            }
        }
        return Promise.resolve();
    }

    private getReusableMediaResource(forwardNavigation: boolean): RES {

        const reusableMediaResource: RES[] = this.generatedMediaResource//.filter(e => e.info.id != currentMediaRes.info.id);

        const reuseMediaRes = this.generatedMediaResource.length > 1 ? (forwardNavigation ? reusableMediaResource.shift() : reusableMediaResource.pop()) || null : null;
        if (reuseMediaRes) {

            this.playerControls.clearPlayerMediaElement(reuseMediaRes.info.id);

            if (reuseMediaRes.info.id in this.preloadedResourcesMap) {
                delete this.preloadedResourcesMap[reuseMediaRes.info.id];
            }
        }
        return reuseMediaRes;
    }

    private appendReuseableMediaResouce(mediaRes: RES, forwardNavigation: boolean) {
        if (this.generatedMediaResource.find(e => e.info.id == mediaRes.info.id) == undefined) {
            // if (forwardNavigation)
            this.generatedMediaResource.push(mediaRes);
            // else
            //     this.reusableMediaResource.unshift(mediaRes)

        }
    }

    private generatePlayerMediaResource(mediaInfo: MI, pve: MediaPlayerElements<EL>, forwardNavigation: boolean) {
        if (this.config.enableReusingMediaResource) {
            const useMediaResource = this.getReusableMediaResource(forwardNavigation);
            if (useMediaResource) {
                return this.reinitMediaResource(mediaInfo, pve, useMediaResource);
            }
        }

        return this.initMediaResource(mediaInfo, pve);
    }

    public unloadContext(mediaID: string) {
        if (this.config.unloadManagementStrategy == UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE) {
            if (mediaID in this.preloadedResourcesMap) {
                this.logger.debug("canceling ", mediaID);
                this.preloadedResourcesMap[mediaID].cancel();
            }
        }
    }

    protected abstract buildPlayerMediaContext(res: RES, playerMediaEls: MediaPlayerElements<EL>): CTX


    protected sourcesFromMediaInfo(mediaInfo: MI): { src: string }[] {
        return mediaInfo.format.map(f => {
            const s = { src: f.resourceUrl }
            return s
        })
    }

    protected abstract initMediaResource(mediaInfo: MI,
        playerMediaEls: MediaPlayerElements<EL>,
    ): CancelablePromise<RES>;

    protected abstract reinitMediaResource(mediaInfo: MI,
        playerMediaEls: MediaPlayerElements<EL>,
        mediaRes: RES
    ): CancelablePromise<RES>;
}


export interface PlayerTechFactoryInterface<EL extends HTMLMediaElement, MI extends MediaInfoRawData<any>, REF extends MediaRef<EL, MI>,
    CTX extends MediaContext<REF>,> {
    readonly config: PlayerTechFactoryConfig

    getOrPreloadMediaContext(mediaInfo: MI,
        playerMediaEls: MediaPlayerElements<EL>,
        forwardNavigation: boolean,
        beforePreload: (mediaIndo: MI) => MI
    ): CancelablePromise<CTX>;
}

export function isPlayerTechFactoryInterface(x: any): x is PlayerTechFactoryInterface<any, any, any, any> {
    return isObject(x) &&
        ("getOrPreloadMediaContext" in x) && isFunction(x.getOrPreloadMediaContext);
}

